import styled, { keyframes } from 'styled-components';
import { BREAKPOINT_SM } from '../../util/breakpoints';
import { scrollbarStyle } from '../../util/scrollbarStyle';
import { BACKDROP_ZINDEX } from '../../util/zIndex';
import type { Variant } from './Modal2';
interface Props {
  mobileVariant?: Variant;
  desktopVariant?: Variant;
  zIndex?: number;
  transitionReverse?: boolean;
  padding?: string;
  margin?: string;
}
const _mediaQueryForStyle = (variant: Variant) => ({
  mobileVariant = 'modal',
  desktopVariant = 'modal'
}: Props) => {
  if (mobileVariant === variant && desktopVariant === variant) {
    return 'all';
  } else if (mobileVariant == variant) {
    return `(max-width: calc(calc(${BREAKPOINT_SM}) - 1px))`;
  } else if (desktopVariant === variant) {
    return `(min-width: calc(${BREAKPOINT_SM}))`;
  } else {
    return 'not all';
  }
};
export const ModalCloseButton = styled.button<Props>`
  border: none;
  display: none;
  height: 32px;
  width: 32px;
  cursor: pointer;
  .close-icon {
    height: 20px;
    width: 20px;
  }
  z-index: ${({
  zIndex
}) => zIndex};

  @media ${_mediaQueryForStyle('modal')} {
    background-color: transparent;
    display: block;
    position: absolute;
    right: 5px;
    top: 5px;
  }
  @media ${_mediaQueryForStyle('full')} {
    background-color: transparent;
    display: block;
    position: fixed;
    right: 15px;
    top: 5px;
  }
  @media ${_mediaQueryForStyle('side')} {
    background-color: transparent;
    display: block;
    position: sticky;
    left: calc(100% - 32px - 5px);
    top: 5px;
  }
  @media ${_mediaQueryForStyle('bottom')} {
    background-color: transparent;
    display: block;
    position: absolute;
    right: 20px;
    top: 20px;
  }
`;
export const ModalDialog = styled.div<Props>`
  display: none;

  @media ${_mediaQueryForStyle('modal')} {
    align-items: center;
    display: flex;
    justify-content: center;
    min-height: 100%;
  }

  @media ${_mediaQueryForStyle('full')} {
    display: block;
    min-height: 100%;
  }

  @media ${_mediaQueryForStyle('side')} {
    display: block;
    min-height: 100%;
  }

  @media ${_mediaQueryForStyle('block')} {
    display: block;
  }

  @media ${_mediaQueryForStyle('bottom')} {
    display: block;
  }
`;
const modalContentKeyframes = keyframes`
  from{
    opacity: 0;
    transform: translate3d(0, -30%, 0);
  }
  to{
    opacity:1;
  }
`;
export const ModalCard = styled.div<Props>`
  display: none;

  @media ${_mediaQueryForStyle('modal')} {
    animation: ${modalContentKeyframes} 0.4s ease-in-out;
    background-color: white;

    color: ${({
  theme
}) => theme?.colors?.textColor ?? '#273240'};
    display: block;
    min-height: 140px;
    position: relative;

    @media (max-width: calc(${BREAKPOINT_SM} - 1px)) {
      margin: ${({
  margin
}) => margin ?? '20px 0'};
      width: 370px;
    }

    @media (min-width: calc(${BREAKPOINT_SM})) {
      border-radius: 4px;
      margin: ${({
  margin
}) => margin ?? '50px'};
      max-width: 1000px;
      min-width: 400px;
    }
    &.hidden {
      opacity: 0;
      transform: translate3d(0, -30%, 0);
      transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
    }
  }
  @media ${_mediaQueryForStyle('full')} {
    display: block;
    position: relative;
  }
  @media ${_mediaQueryForStyle('side')} {
    display: block;
    min-width: 400px;
    position: relative;
  }
  @media ${_mediaQueryForStyle('block')} {
    display: block;
  }
  @media ${_mediaQueryForStyle('bottom')} {
    background-color: white;
    color: ${({
  theme
}) => theme?.colors?.textColor ?? '#273240'};
    display: block;
    min-height: 200px;
    position: relative;
    border-radius: 20px 20px 0px 0px;
  }
`;
export const ModalContent = styled.div`
  @media ${_mediaQueryForStyle('modal')} {
    @media (max-width: calc(${BREAKPOINT_SM} - 1px)) {
      padding: ${({
  padding
}) => padding ?? '40px 30px 20px'};
    }

    @media (min-width: calc(${BREAKPOINT_SM})) {
      padding: ${({
  padding
}) => padding ?? '30px 30px'};
    }
  }
  @media ${_mediaQueryForStyle('full')} {
    padding: ${({
  padding
}) => padding ?? '20px'};
  }
  @media ${_mediaQueryForStyle('side')} {
    padding: ${({
  padding
}) => padding ?? '20px'};
  }
`;
const fullContainerKeyframes = keyframes`
  from{
    transform: translate3d(0, 100%, 0);
    /* opacity:0; */
  }
  to{
    /* opacity:1; */
  }
`;
const fullContainerKeyframesReverse = keyframes`
  from{
    transform: translate3d(0, -100%, 0);
    /* opacity:0; */
  }
  to{
    /* opacity:1; */
  }
`;
const sideContainerKeyframes = (side: 'left' | 'right') => keyframes`
  from{
    opacity:0;
    transform: ${side === 'left' ? `translate3d(-100%, 0, 0)` : `translate3d(100%, 0, 0)`} ;
  }
  to{
    opacity:1;
  }
`;
const blockContainerKeyframes = keyframes`
  from{
    opacity:0;
  }
  to{
    opacity:1;
  }
`;
const bottomContainerKeyframes = keyframes`
  from{
    opacity:0;
    transform: translate3d(0, 100%, 0);
  }
  to{
    opacity:1;
  }
`;
export const ModalContainer = styled.div<Props>`
  display: none;

  @media ${_mediaQueryForStyle('modal')} {
    align-items: center;
    bottom: 0;
    display: block;
    justify-content: center;
    left: 0;
    outline: 0;
    overflow-x: hidden;
    overflow-y: auto;
    position: fixed;
    right: 0;
    top: 0;
    z-index: ${({
  zIndex
}) => zIndex};
  }

  @media ${_mediaQueryForStyle('full')} {
    animation: ${({
  transitionReverse
}) => transitionReverse ? fullContainerKeyframesReverse : fullContainerKeyframes}
      0.4s ease-in-out;
    background-color: white;
    color: ${({
  theme
}) => theme?.colors?.textColor ?? '#273240'};
    bottom: 0;
    display: block;
    left: 0;
    outline: 0;
    overflow-x: hidden;
    overflow-y: auto;
    position: fixed;
    right: 0;
    top: 0;
    z-index: ${({
  zIndex
}) => zIndex};
    ${scrollbarStyle}

    &.hidden {
      transition: transform 0.3s ease-in-out;
      transform: translate3d(0, ${({
  transitionReverse
}) => transitionReverse ? '-100%' : '100%'}, 0);
      /* opacity: 0; */
    }
  }
  @media ${_mediaQueryForStyle('side')} {
    background-color: white;
    color: ${({
  theme
}) => theme?.colors?.textColor ?? '#273240'};
    bottom: 0;
    display: block;
    outline: 0;
    overflow-x: hidden;
    overflow-y: auto;
    position: fixed;
    &.side-position-left {
      left: 0;
      animation: ${sideContainerKeyframes('left')} 0.4s ease-in-out;
    }
    &.side-position-right {
      right: 0;
      animation: ${sideContainerKeyframes('right')} 0.4s ease-in-out;
    }
    top: 0;
    z-index: ${({
  zIndex
}) => zIndex};
    ${scrollbarStyle}

    @media (max-width: calc(${BREAKPOINT_SM})) {
      min-width: 320px;
    }

    &.hidden.side-position-left {
      transform: translate3d(-100%, 0, 0);
    }
    &.hidden.side-position-right {
      transform: translate3d(100%, 0, 0);
    }

    &.hidden {
      opacity: 0;
      transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
    }
  }

  @media ${_mediaQueryForStyle('block')} {
    display: block;
    transition: opacity 0.3s ease-in-out;

    &.appearing {
      animation: ${blockContainerKeyframes} 0.4s ease-in-out;
    }
    &.hidden {
      opacity: 0;
    }
  }

  @media ${_mediaQueryForStyle('bottom')} {
    animation: ${bottomContainerKeyframes} 0.4s ease-in-out;
    bottom: 0;
    display: block;
    justify-content: center;
    left: 0;
    outline: 0;
    overflow-x: hidden;
    overflow-y: auto;
    position: fixed;
    right: 0;
    left: 0;
    z-index: ${({
  zIndex
}) => zIndex};

    &.hidden {
      opacity: 0;
      transform: translate3d(0, 100%, 0);
      transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
    }
  }
`;
const modalBackdropKeyframes = keyframes`
  from{
    opacity:0;
  }
  to{
    opacity:.5;
  }
`;
export const ModalBackdrop = styled.div<Props>`
  display: none;

  @media ${_mediaQueryForStyle('modal')}, ${_mediaQueryForStyle('side')}, ${_mediaQueryForStyle('bottom')} {
    animation: ${modalBackdropKeyframes} 0.2s ease-in-out;
    background-color: #000;
    bottom: 0;
    display: block;
    left: 0;
    opacity: 0.5;
    position: fixed;
    right: 0;
    top: 0;
    z-index: ${BACKDROP_ZINDEX};

    &.hidden {
      opacity: 0;
      transition: opacity 0.3s ease-in-out;
    }
  }
`;